<template>
  <v-container fluid>
    <v-card tile>
      <div class="pl-6">
        <p class="text-body-1 font-weight-regular pt-4">
          Canal Web — Reconocimiento Facial: {{ totalRegistros }} gestiones
        </p>
        <p class="text-caption text--secondary font-weight-bold mt-n3 pb-4" v-if="selectedFecha && selectedFecha[0]">
          <v-icon color="black">mdi-calendar-range</v-icon>
          del {{ selectedFecha[0] }} al {{ selectedFecha[1] }}
        </p>
        <p v-else class="text-caption text--secondary pb-4"></p>
      </div>
    </v-card>

    <v-row>
      <!-- Tabla -->
      <v-col cols="12">
        <v-data-table
          disable-pagination
          :options.sync="options"
          :hide-default-footer="true"
          :headers="headers"
          :items="datosTabla"
          v-model="selected"
          show-select
          item-key="id"
          class="elevation-2"
          @click:row="handleClick"
        >
          <template v-slot:top>
            <v-row no-gutters align="center" class="pa-2">
              <v-chip v-if="selected.length" color="#031D6A" dark class="mr-2">
                {{ selected.length }} gestion{{ selected.length === 1 ? '' : 'es' }} seleccionada{{ selected.length === 1 ? '' : 's' }}
              </v-chip>
              <v-btn
                v-if="selected.length"
                x-small dark color="#031D6A" class="mr-2"
                :loading="descargandoZip" :disabled="descargandoZip"
                @click="fnDescargarAutorizacionesMasivo"
              >
                <v-icon small left>mdi-folder-zip-outline</v-icon> Descargar autorizaciones (PDF)
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn x-small @click="exportToExcel" color="success">
                Exportar a Excel
              </v-btn>
            </v-row>
          </template>

          <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
            <v-tooltip bottom v-if="!item.consentimiento_buro_at">
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs" @click.stop>
                  <v-simple-checkbox :value="false" disabled></v-simple-checkbox>
                </span>
              </template>
              <span>Sin consentimiento de buró registrado — no se puede descargar</span>
            </v-tooltip>
            <v-simple-checkbox v-else :value="isSelected" @input="select($event)" @click.native.stop></v-simple-checkbox>
          </template>

          <template v-slot:body.prepend>
            <tr>
              <td></td>
              <td>
                <v-text-field v-model="id" dense hide-details single-line clearable
                  placeholder="ID" @keydown.enter="fnAplicarParametros()"></v-text-field>
              </td>
              <td>
                <v-text-field v-model="documentoIdentificacion" dense hide-details single-line clearable
                  placeholder="DPI" @keydown.enter="fnAplicarParametros()"></v-text-field>
              </td>
              <td>
                <v-text-field v-model="nombre" dense hide-details single-line clearable
                  placeholder="Nombre" @keydown.enter="fnAplicarParametros()"></v-text-field>
              </td>
              <td>
                <v-text-field v-model="telefono" dense hide-details single-line clearable
                  placeholder="Teléfono" @keydown.enter="fnAplicarParametros()"></v-text-field>
              </td>
              <td></td>
              <td>
                <v-select v-model="resultadoEvaluacion" :items="itemsResultadoEvaluacion" dense hide-details
                  single-line clearable placeholder="Resultado" @change="fnAplicarParametros"></v-select>
              </td>
              <td></td>
              <td>
                <v-menu ref="fechaDialog" v-model="modalFechas" :close-on-content-click="false"
                  transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="selectedFecha" readonly dense hide-details single-line clearable
                      placeholder="Fecha creación" prepend-icon="mdi-calendar" v-bind="attrs" v-on="on"></v-text-field>
                  </template>
                  <v-date-picker locale="es" width="240" no-title scrollable
                    v-model="dialogVarSelectedFecha" range
                    :allowed-dates="fnAllowedDates" :max="disabledFecha"
                    color="primary" header-color="primary">
                    <p class="text-caption text-center mb-0 px-2 grey--text">Rango máximo: 30 días</p>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="modalFechas = false">Cancelar</v-btn>
                    <v-btn text color="primary" @click="$refs.fechaDialog.save(dialogVarSelectedFecha)">OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </td>
              <td></td>
            </tr>
          </template>

          <template v-slot:[`item.resultado_evaluacion`]="{ item }">
            <v-chip v-if="item.resultado_evaluacion === 'rechazado'" color="red" dark>ROJO</v-chip>
            <v-chip v-else-if="item.resultado_evaluacion === 'revisión'" color="yellow accent-2">AMARILLO</v-chip>
            <v-chip v-else-if="item.resultado_evaluacion === 'aprobado'" color="green" dark>VERDE</v-chip>
            <span v-else class="text--disabled">—</span>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ fnGetDateTime(item.createdAt) }}
          </template>
          <template v-slot:[`item.fecha_de_nacimiento`]="{ item }">
            {{ fnGetDateUTC(item.fecha_de_nacimiento) }}
          </template>
          <template v-slot:[`item.ver`]="{ item }">
            <v-btn x-small dark color="#031D6A" @click.stop="fnVerRegistro(item)">VER</v-btn>
          </template>
        </v-data-table>

        <!-- Paginación -->
        <v-col cols="12">
          <v-col offset="9" cols="3">
            <v-select v-model="pageSize" :items="pageSizes" label="Registros por página" @change="handlePageSizeChange"></v-select>
          </v-col>
          <v-col>
            <template v-if="pageSize !== 'Todas'">
              Total de registros: {{ totalRegistros }}
              <v-pagination
                class="float-right"
                v-model="page"
                :length="totalPages"
                total-visible="15"
                next-icon="mdi-menu-right"
                prev-icon="mdi-menu-left"
                @input="handlePageChange"
              ></v-pagination>
            </template>
            <template v-else>
              Mostrando todas las {{ totalRegistros }} gestiones que cumplen los filtros.
            </template>
          </v-col>
        </v-col>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment';
require('moment/locale/es.js');
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
  name: 'gestionesReconocimientoFacial',
  data() {
    return {
      options: {},
      page: 1,
      totalPages: 0,
      pageSize: 50,
      pageSizes: [50, 300, 'Todas'],
      totalRegistros: 0,
      datosTabla: [],
      selected: [],
      descargandoZip: false,

      headers: [
        { text: 'No. gestión', value: 'id', sortable: true, type: 'integer' },
        { text: 'DPI', value: 'dpi', sortable: false },
        { text: 'Nombre completo', value: 'deudor_nombre_completo', sortable: false, width: 200 },
        { text: 'Teléfono', value: 'numero_de_celular', sortable: false },
        { text: 'Correo', value: 'correo_electronico', sortable: false, width: 180 },
        { text: 'Resultado evaluación', value: 'resultado_evaluacion', sortable: false },
        { text: 'Estado', value: 'estado', sortable: false },
        { text: 'Fecha creación', value: 'createdAt', sortable: false, width: 180 },
        { text: 'Ver', align: 'center', value: 'ver', sortable: false },
      ],

      // Búsqueda
      id: null,
      documentoIdentificacion: null,
      nombre: null,
      telefono: null,
      resultadoEvaluacion: null,
      itemsResultadoEvaluacion: ['VERDE', 'AMARILLO', 'ROJO'],

      // Fechas
      modalFechas: false,
      dialogVarSelectedFecha: null,
      selectedFecha: [null, null],
      disabledFecha: moment().format('YYYY-MM-DD'),
    };
  },

  computed: {
    feathersServer() {
      return process.env.VUE_APP_FEATHERS_SERVER;
    },
  },

  methods: {
    handleClick(value) {
      this.fnVerRegistro(value);
    },

    fnGetDateTime(date) {
      return date ? moment(date).format('DD-MM-YYYY HH:mm:ss') : '';
    },
    fnGetDateUTC(date) {
      return date ? moment(date).utc().format('DD-MM-YYYY') : '';
    },

    // Restringe la segunda fecha clickeada del rango a máximo 30 días de la primera
    fnAllowedDates(dateStr) {
      if (!this.dialogVarSelectedFecha || this.dialogVarSelectedFecha.length !== 1) return true;
      const base = moment(this.dialogVarSelectedFecha[0]);
      const diff = Math.abs(moment(dateStr).diff(base, 'days'));
      return diff <= 30;
    },

    ...mapActions('GestionesReconocimientoFacial', {
      findGestiones: 'find',
    }),

    getFiltros() {
      const params = {};

      if (this.options.sortBy && this.options.sortBy.length === 1 && this.options.sortDesc.length === 1) {
        const sorting = {};
        sorting[this.options.sortBy[0]] = this.options.sortDesc[0] ? -1 : 1;
        params['$sort'] = sorting;
      } else {
        params['$sort'] = { id: -1 };
      }

      if (this.id) params['id'] = this.id;

      if (this.documentoIdentificacion) {
        params['dpi'] = { $like: '%' + this.documentoIdentificacion + '%' };
      }

      if (this.nombre) {
        const palabras = this.nombre.split(' ').filter(Boolean);
        params['$and'] = palabras.map(p => ({
          $or: [
            { deudor_nombre_completo: { $like: `%${p}%` } },
            { primer_nombre:          { $like: `%${p}%` } },
            { primer_apellido:        { $like: `%${p}%` } },
          ]
        }));
      }

      if (this.telefono) {
        params['numero_de_celular'] = { $like: '%' + this.telefono + '%' };
      }

      if (this.resultadoEvaluacion) {
        const map = { VERDE: 'aprobado', AMARILLO: 'revisión', ROJO: 'rechazado' };
        params['resultado_evaluacion'] = map[this.resultadoEvaluacion];
      }

      if (this.selectedFecha && this.selectedFecha[0] && this.selectedFecha[1]) {
        const [s1, s2] = [this.selectedFecha[0].split('-'), this.selectedFecha[1].split('-')];
        params['createdAt'] = {
          $gte: new Date(s1[2], s1[1] - 1, s1[0], 0, 0, 0),
          $lte: new Date(s2[2], s2[1] - 1, s2[0], 23, 59, 59),
        };
      }

      return params;
    },

    getParams() {
      const params = this.getFiltros();

      if (this.pageSize === 'Todas') {
        params['$limit'] = this.totalRegistros || 10000;
        params['$skip'] = 0;
      } else {
        params['$limit'] = this.pageSize;
        params['$skip']  = (this.page - 1) * this.pageSize;
      }

      return params;
    },

    main() {
      this.findGestiones({ query: this.getParams() }).then(r => {
        this.datosTabla     = r.data;
        this.totalRegistros = r.total;
        this.selected        = [];
        this.totalPages = this.pageSize === 'Todas'
          ? 1
          : Math.ceil(parseInt(r.total) / parseInt(this.pageSize));
      });
    },

    async fnActualizarTotalParaTodas() {
      const probe = await this.findGestiones({ query: { ...this.getFiltros(), $limit: 1, $skip: 0 } });
      this.totalRegistros = probe.total;
    },

    handlePageChange(value) { this.page = value; this.main(); },

    async handlePageSizeChange(size) {
      this.page = 1;
      this.pageSize = size;
      if (size === 'Todas') {
        await this.fnActualizarTotalParaTodas();
      }
      this.main();
    },

    async fnAplicarParametros() {
      this.page = 1;
      if (this.pageSize === 'Todas') {
        await this.fnActualizarTotalParaTodas();
      }
      this.main();
    },

    fnVerRegistro(data) {
      this.$router.push('/detalle-reconocimiento-facial/' + data.id);
    },

    async fnDescargarAutorizacionesMasivo() {
      if (!this.selected.length) return;
      this.descargandoZip = true;
      try {
        const url = `${this.feathersServer}generar-autorizacion-buro-pdf-masivo`;
        const response = await axios.post(
          url,
          { ids: this.selected.map(g => g.id) },
          {
            responseType: 'blob',
            timeout: 120000,
            headers: { Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt') },
          }
        );
        const urlDown = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = urlDown;
        link.setAttribute('download', `autorizaciones-buro-${moment().format('YYYYMMDD-HHmmss')}.zip`);
        document.body.appendChild(link);
        link.click();
      } catch (err) {
        console.error('Error al descargar autorizaciones masivo:', err);
      } finally {
        this.descargandoZip = false;
      }
    },

    async exportToExcel() {
      try {
        const XLSX = require('xlsx');
        const params = this.getParams();
        params['$limit'] = this.totalRegistros;
        params['$skip']  = 0;
        const response = await this.findGestiones({ query: params });

        const excelData = response.data.map(record => {
          const row = {};
          this.headers.forEach(h => {
            if (h.text !== 'Ver') {
              if (h.value === 'createdAt') row[h.text] = this.fnGetDateTime(record[h.value]);
              else row[h.text] = record[h.value] || '';
            }
          });
          return row;
        });

        const ws = require('xlsx').utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Reconocimiento');
        XLSX.writeFile(wb, 'gestiones-reconocimiento-facial.xlsx');
      } catch (error) {
        console.error('Error al exportar a Excel:', error);
      }
    },
  },

  watch: {
    options: {
      handler() { this.main(); },
      deep: true,
      immediate: true,
    },
    // El checkbox "seleccionar todo" del header no pasa por el slot item.data-table-select,
    // así que puede marcar gestiones sin consentimiento_buro_at — se filtran aquí como respaldo.
    selected(newVal) {
      const validos = newVal.filter(g => g.consentimiento_buro_at);
      if (validos.length !== newVal.length) this.selected = validos;
    },
    dialogVarSelectedFecha(newValue) {
      if (newValue && newValue.length === 2) {
        this.selectedFecha = [
          moment(newValue[0]).format('DD-MM-YYYY'),
          moment(newValue[1]).format('DD-MM-YYYY'),
        ];
      } else {
        this.selectedFecha = [null, null];
      }
    },
  },

  mounted() {
    this.main();
  },
};
</script>

<style scoped>
table { width: 100%; border-collapse: collapse; }
th, td { padding: 2px; }
</style>
